import { Fragment, type ReactNode, useMemo } from "react";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// Filters and detail-sheet pieces shared by the coverage matrix pages (Catalog and Token Factory).

// A deep-linked sha that aged out of the observed list stays selectable so
// the trigger renders it and the user can clear it (same rule as the tag).
export function shaOptions(observed: string[] | undefined, selected: string | null): string[] {
  const shas = [...(observed ?? [])];
  if (selected && !shas.includes(selected)) shas.unshift(selected);
  return shas;
}

const NO_SHA = "No sha";

export function SearchableFilter({
  label,
  value,
  options,
  onChange,
  noneLabel = NO_SHA,
  noun = "sha",
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (value: string) => void;
  noneLabel?: string;
  noun?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <Combobox
        value={value ?? ""}
        onValueChange={(next) => onChange(next === noneLabel ? "" : next)}
        options={[noneLabel, ...options]}
        placeholder={noneLabel}
        searchPlaceholder={`Search ${noun}…`}
        emptyText={`No matching ${noun}`}
        className="font-mono text-xs"
      />
    </div>
  );
}

export function LabeledSelect({
  label,
  value,
  options,
  onChange,
  renderLabel,
  triggerClassName = "w-[150px]",
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  renderLabel: (value: string) => string;
  triggerClassName?: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <Select value={value} onValueChange={(next) => next && onChange(next)}>
        <SelectTrigger className={cn("h-8", triggerClassName)}>
          <SelectValue>{(current) => renderLabel(String(current))}</SelectValue>
        </SelectTrigger>
        <SelectContent align="start" alignItemWithTrigger={false} className="min-w-[10rem]">
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {renderLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// --- Drill-in: full declared config for one cell ---

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}
function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string");
}
function asNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

interface Invocation {
  image?: string;
  command?: string[];
  env?: Array<[string, string]>;
}

// The launch command + env for a cell, from the engine workload (vLLM / SGLang /
// TRT-LLM) or, for Dynamo (DGD) tests, the first worker service that carries a
// command/args.
function extractInvocation(config: Record<string, unknown>): Invocation {
  const workload = asRecord(config.workload);
  if (workload) {
    const envRecord = asRecord(workload.env);
    return {
      image: asString(workload.image),
      command: asStringArray(workload.command),
      env: envRecord
        ? Object.entries(envRecord).map(([key, value]) => [key, String(value)] as [string, string])
        : undefined,
    };
  }
  const services = asRecord(asRecord(config.dgd)?.services);
  for (const service of Object.values(services ?? {})) {
    const serviceRecord = asRecord(service);
    const command = asStringArray(serviceRecord?.command);
    const args = asStringArray(serviceRecord?.args);
    if (!command && !args) continue;
    const rawEnvs = Array.isArray(serviceRecord?.envs) ? serviceRecord.envs : [];
    const env = rawEnvs
      .map((entry) => asRecord(entry))
      .filter((entry): entry is Record<string, unknown> => entry != null)
      .map((entry) => [String(entry.name ?? ""), String(entry.value ?? "")] as [string, string])
      .filter(([key]) => key.length > 0);
    return {
      image: asString(serviceRecord?.image),
      command: [...(command ?? []), ...(args ?? [])],
      env: env.length > 0 ? env : undefined,
    };
  }
  return {};
}

// Split a flat argv into readable lines: each `--flag value` pair (or bare flag)
// on its own line, so a long vLLM launch reads top-to-bottom.
function groupArgs(tokens: string[]): string[] {
  const lines: string[] = [];
  let index = 0;
  while (index < tokens.length) {
    const token = tokens[index];
    if (token === undefined) break;
    const next = tokens[index + 1];
    if (token.startsWith("-") && next != null && !next.startsWith("-")) {
      lines.push(`${token} ${next}`);
      index += 2;
    } else {
      lines.push(token);
      index += 1;
    }
  }
  return lines;
}

function CommandView({ command }: { command: string[] }) {
  const lines = useMemo(() => groupArgs(command), [command]);
  return (
    <pre className="overflow-x-auto rounded-md bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
      {lines.map((line, index) => {
        const spaceAt = line.indexOf(" ");
        const flag = spaceAt === -1 ? line : line.slice(0, spaceAt);
        const value = spaceAt === -1 ? "" : line.slice(spaceAt + 1);
        return (
          <div key={`${index}:${line}`} className={index === 0 ? "" : "pl-4"}>
            <span
              className={
                flag.startsWith("-")
                  ? "text-sky-600 dark:text-sky-400"
                  : "font-medium text-foreground"
              }
            >
              {flag}
            </span>
            {value && <span className="text-foreground/80"> {value}</span>}
          </div>
        );
      })}
    </pre>
  );
}

function EnvView({ env }: { env: Array<[string, string]> }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md bg-muted/50 p-3 font-mono text-[11px]">
      {env.map(([key, value]) => (
        <Fragment key={key}>
          <span className="text-amber-600 dark:text-amber-400">{key}</span>
          <span className="break-all text-foreground/80">{value}</span>
        </Fragment>
      ))}
    </div>
  );
}

// Launch command, env, timeouts, verify and the raw resolved config of a test.
export function CellConfigSections({ config }: { config: Record<string, unknown> }) {
  const invocation = extractInvocation(config);
  const verifyUrl = asString(asRecord(config.verify)?.url);
  const timeouts: Array<[string, number | undefined]> = [
    ["checkpoint", asNumber(config.checkpoint_timeout)],
    ["restore", asNumber(config.restore_timeout)],
    ["dump", asNumber(config.dump_timeout)],
    ["restore_criu", asNumber(config.restore_criu_timeout)],
  ];
  return (
    <>
      {invocation.image || invocation.command?.length || invocation.env?.length ? (
        <Section title="Invocation">
          {invocation.image && (
            <DetailRow
              label="Image"
              value={<span className="break-all font-mono text-xs">{invocation.image}</span>}
            />
          )}
          {invocation.command && invocation.command.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Command
              </div>
              <CommandView command={invocation.command} />
            </div>
          )}
          {invocation.env && invocation.env.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Env
              </div>
              <EnvView env={invocation.env} />
            </div>
          )}
        </Section>
      ) : null}

      {timeouts.some(([, value]) => value != null) && (
        <Section title="Timeouts (s)">
          {timeouts.map(([label, value]) =>
            value != null ? <DetailRow key={label} label={label} value={String(value)} /> : null,
          )}
        </Section>
      )}

      {verifyUrl && (
        <Section title="Verify">
          <DetailRow label="URL" value={<span className="font-mono text-xs">{verifyUrl}</span>} />
        </Section>
      )}

      <details className="rounded-md border border-border">
        <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          Raw config (JSON)
        </summary>
        <pre className="overflow-x-auto px-3 pb-3 font-mono text-[11px] leading-relaxed">
          {JSON.stringify(config, null, 2)}
        </pre>
      </details>
    </>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground/80">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 break-words text-right text-sm">{value}</span>
    </div>
  );
}
